import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from '@/components/ui/input-otp';
import { toast } from '@/hooks/use-toast';
import z, { useZodForm } from '@/lib/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS, REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { ArrowRight, DatabaseBackup, Key, Loader } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    mfaEmailVerifyLoginMutaionFn,
    verifyBackupCodesMutationFn,
    verifyMfaTotpLoginMutationFn,
} from '@/lib/api';
import { MFAMethod } from '@/types/enums';
import React, { HTMLAttributes, useState } from 'react';

const VerifyMfa = () => {
    const location = useLocation();
    const {
        message,
        method,
        isEmailSent,
        email,
        fromPicker,
        direct,
        isDeactivated,
    } = location.state || {};
    const [isUsingBackupCodes, setIsUsingBackupCodes] = useState(
        (direct as boolean) || false,
    );
    const navigate = useNavigate();

    const queryClient = useQueryClient();
    const otpCellStyle: HTMLAttributes<HTMLElement>['className'] =
        'w-7 h-8 !text-sm sm:!w-14 sm:!h-12 md:!text-lg';

    const { mutate, isPending } = useMutation({
        mutationFn: isUsingBackupCodes
            ? verifyBackupCodesMutationFn
            : method === MFAMethod.EMAIL
              ? mfaEmailVerifyLoginMutaionFn
              : verifyMfaTotpLoginMutationFn,
    });

    const MFAverifyFormSchema = z.object({
        pin: z.string().min(isUsingBackupCodes ? 10 : 6, {
            message: isUsingBackupCodes
                ? 'Your backup code should be 10 characters.'
                : 'Your one-time pin code must be 6 digits.',
        }),
    });

    const mfaVerifyForm = useZodForm(MFAverifyFormSchema, {
        defaultValues: {
            pin: '',
        },
    });

    const onSubmitMFAverify = async (
        values: z.infer<typeof MFAverifyFormSchema>,
    ) => {
        if (!email && !method) {
            navigate('/', { state: {}, replace: true });
            return;
        }
        const data = {
            code: values.pin,
            email: email || undefined,
            method: method || undefined,
        };
        mutate(data, {
            onSuccess: (response) => {
                queryClient.resetQueries({
                    queryKey: ['authUser'],
                });
                navigate('/mfa', { state: {}, replace: true });
                toast({
                    title: `Hello, ${response?.data.user.name || 'Friend'}!`,
                    description: response?.data?.message,
                });
            },
            onError: (error) => {
                if (
                    'errorCode' in error &&
                    error.errorCode === 'AUTH_UNAUTHORIZED_ACCESS'
                ) {
                    toast({
                        title: 'Error',
                        description: error.message,
                        variant: 'destructive',
                    });
                } else {
                    toast({
                        title: 'Not Allowed',
                        description: error.message,
                    });
                    location.state = {};
                }
            },
        });
    };

    function handleGoBackToLoging() {
        navigate('/login', {
            state: {
                fromPicker,
                email,
            },
            replace: true,
        });
    }

    React.useEffect(() => {
        if (!method) {
            navigate('/login', { state: {}, replace: true });
        }

        if (!direct && !isUsingBackupCodes && method) {
            toast({
                itemID: 'mfa_toast',
                title: 'MFA Code Verification Required',
                description: isEmailSent
                    ? `Email with pin code has been sent to ${email}`
                    : 'Enter the generated pin code by authenticator app.',
            });
        }
    }, [direct, email, isUsingBackupCodes, isEmailSent, method, navigate]);

    return method ? (
        <main className='w-full px-2 min-h-[590px] h-full flex items-center justify-center'>
            <div className='h-full rounded-md'>
                <Logo size='100px' />
                {message && (
                    <h1
                        className='text-fluid-h1 tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mt-8
        text-center'>
                        {message}
                    </h1>
                )}
                {isDeactivated && (
                    <h1
                        className='text-sm md:text-lg tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mt-8
        text-center'>
                        Your account is deactivated
                        <br />
                        Please follow the instructions <br />
                        to restore your account
                    </h1>
                )}
                <h1
                    className='text-fluid-h1 tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mt-8
        text-center'>
                    Multi-Factor Authentication
                </h1>
                {isUsingBackupCodes ? (
                    <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                        Enter an earlier saved unused Backup Code
                    </p>
                ) : (
                    <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                        Enter the pin code{' '}
                        {method === MFAMethod.EMAIL
                            ? `which sent to ${email}`
                            : 'generated by authenticator app'}
                        .
                    </p>
                )}
                <div className='mt-2'>
                    <Form {...mfaVerifyForm}>
                        <form
                            onSubmit={mfaVerifyForm.handleSubmit(
                                onSubmitMFAverify,
                            )}
                            className='w-full mt-6 flex flex-col gap-4 '>
                            <FormField
                                control={mfaVerifyForm.control}
                                name='pin'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col items-center'>
                                        <FormLabel className='mb-1 font-normal text-muted-foreground w-full'>
                                            <div className='w-full flex flex-col items-center gap-2 px-4 md:text-[1rem]'>
                                                {isUsingBackupCodes ? (
                                                    <>
                                                        <DatabaseBackup
                                                            size={18}
                                                        />{' '}
                                                        <span>Backup Code</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Key size={18} />
                                                        <span>Pin Code</span>
                                                    </>
                                                )}
                                            </div>
                                        </FormLabel>
                                        <FormControl>
                                            <InputOTP
                                                className='!text-lg flex !flex-col sm:!flex-row items-center'
                                                maxLength={
                                                    isUsingBackupCodes ? 10 : 6
                                                }
                                                pattern={
                                                    isUsingBackupCodes
                                                        ? REGEXP_ONLY_DIGITS_AND_CHARS
                                                        : REGEXP_ONLY_DIGITS
                                                }
                                                {...field}
                                                style={{
                                                    justifyContent: 'center',
                                                }}>
                                                <InputOTPGroup className=''>
                                                    {Array.from(
                                                        {
                                                            length: isUsingBackupCodes
                                                                ? 5
                                                                : 3,
                                                        },

                                                        (_, index) => (
                                                            <InputOTPSlot
                                                                key={index}
                                                                index={index}
                                                                className={
                                                                    otpCellStyle
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </InputOTPGroup>
                                                <Separator className='w-6 bg-primary' />
                                                <InputOTPGroup className=''>
                                                    {Array.from(
                                                        {
                                                            length: isUsingBackupCodes
                                                                ? 5
                                                                : 3,
                                                        },

                                                        (_, index) => (
                                                            <InputOTPSlot
                                                                key={index}
                                                                index={
                                                                    (isUsingBackupCodes
                                                                        ? 5
                                                                        : 3) +
                                                                    index
                                                                }
                                                                className={
                                                                    otpCellStyle
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button
                                disabled={isPending}
                                className='w-full h-[40px] mt-2'>
                                {isPending && (
                                    <Loader className='animate-spin' />
                                )}
                                Continue
                                <ArrowRight />
                            </Button>
                        </form>
                    </Form>
                </div>
                <Button
                    variant='ghost'
                    onClick={handleGoBackToLoging}
                    className='w-full text-[15px] mt-2 h-[40px]'>
                    {fromPicker ? 'Choose another method' : 'Return to log in'}
                </Button>
                {!direct ? (
                    <Button
                        variant={'link'}
                        onClick={() => {
                            setIsUsingBackupCodes((prev) => !prev);
                            mfaVerifyForm.reset();
                        }}
                        className='w-full'>
                        {isUsingBackupCodes
                            ? `Back to ${method} authentication method?`
                            : `Don't have access to ${method === MFAMethod.EMAIL ? 'your primary email' : 'authentication app'}?`}
                    </Button>
                ) : null}
            </div>
        </main>
    ) : null;
};

export default VerifyMfa;
